import React, { useRef, useState } from 'react';
import { Box, Button, IconButton, Link, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpenseReceiptDTO } from '../../interfaces/expense.interface';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';
import { saveBlob } from '../../utils/saveBlob';
import { formatAmount } from '../../utils/expenseDisplay';

/** The server (document attachments) accepts PDF, PNG and JPEG up to 5 MB. */
export const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;
export const RECEIPT_ACCEPT = 'image/png,image/jpeg,application/pdf';
const RECEIPT_TYPES = RECEIPT_ACCEPT.split(',');

/** The line needs a receipt when its amount is above the threshold and none is attached yet. */
export const receiptNeeded = (amount: number, threshold: number | null | undefined, hasReceipt: boolean): boolean =>
    !hasReceipt && threshold != null && Number.isFinite(amount) && amount > threshold;

interface Props {
    /** Saved claim id; undefined while the claim has never been saved (nothing to attach the file to yet). */
    claimId?: string;
    receiptId: string | null | undefined;
    fileName?: string;
    amount: number;
    threshold?: number | null;
    onAttached: (receipt: ExpenseReceiptDTO) => void;
    onRemoved: () => void;
}

const ExpenseReceiptControl: React.FC<Props> = ({ claimId, receiptId, fileName, amount, threshold, onAttached, onRemoved }) => {
    const toast = useToast();
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);

    const needed = receiptNeeded(amount, threshold, Boolean(receiptId));
    const noClaim = !claimId;

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !claimId) return;
        if (!RECEIPT_TYPES.includes(file.type)) {
            toast.showError('A receipt must be a PDF, PNG or JPEG file');
            return;
        }
        if (file.size > RECEIPT_MAX_BYTES) {
            toast.showError('A receipt can be at most 5 MB');
            return;
        }
        setBusy(true);
        try {
            onAttached(await ExpenseClaimService.uploadReceipt(claimId, file));
        } catch (err: any) {
            toast.showError(err?.response?.data?.message ?? 'Could not upload the receipt');
        } finally {
            setBusy(false);
        }
    };

    const handleOpen = async () => {
        if (!claimId || !receiptId) return;
        try {
            const { blob, fileName: name } = await ExpenseClaimService.downloadReceipt(claimId, receiptId);
            saveBlob(blob, name);
        } catch {
            toast.showError('Could not open the receipt');
        }
    };

    const handleRemove = async () => {
        if (!claimId || !receiptId) return;
        setBusy(true);
        try {
            await ExpenseClaimService.removeReceipt(claimId, receiptId);
            onRemoved();
        } catch (err: any) {
            toast.showError(err?.response?.data?.message ?? 'Could not remove the receipt');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box data-testid="receipt-control" sx={{ flex: '1 1 220px', maxWidth: 360, minWidth: 0 }}>
            {receiptId ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Link
                        component="button" type="button" variant="body2" onClick={handleOpen}
                        data-help="expense.claimForm.receipt.file" data-testid="receipt-open"
                        sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                    >
                        {fileName ?? 'Receipt'}
                    </Link>
                    <IconButton
                        size="small" onClick={handleRemove} disabled={busy}
                        data-help="expense.claimForm.receipt.remove" aria-label="Remove receipt"
                        {...helpWhy(busy ? { kind: 'status', message: 'Wait for the current receipt action to finish.' } : null)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ) : (
                <>
                    <Button
                        size="small" variant="outlined" startIcon={<AttachFileIcon />}
                        onClick={() => inputRef.current?.click()}
                        disabled={noClaim || busy}
                        data-help="expense.claimForm.receipt.attach"
                        {...helpWhy(noClaim ? { kind: 'status', message: 'Save the draft first. A receipt can only be attached to a saved claim.' }
                            : busy ? { kind: 'status', message: 'Wait for the upload to finish.' } : null)}
                    >
                        {busy ? 'Uploading…' : 'Attach receipt'}
                    </Button>
                    <input
                        ref={inputRef} type="file" hidden accept={RECEIPT_ACCEPT}
                        data-testid="receipt-input" aria-label="Receipt file" onChange={handleFile}
                    />
                </>
            )}
            {!receiptId && noClaim && (
                <Typography variant="caption" color="text.secondary" display="block" data-testid="receipt-save-first">
                    Save the draft to attach a receipt
                </Typography>
            )}
            {needed && (
                <Typography variant="caption" color="warning.main" display="block" data-testid="receipt-needed">
                    Receipt needed: above ৳{formatAmount(threshold)}
                </Typography>
            )}
        </Box>
    );
};

export default ExpenseReceiptControl;
